import { useEffect, useState } from "react";
import { http, resolveImage } from "@/lib/api";

const _cache = {};
const _inflight = {};

/**
 * Returns the admin-saved content override for a public page (key `page_<slug>`).
 * Caches once per session; falls back to null when no override exists.
 */
export function usePageContent(slug) {
  const [data, setData] = useState(_cache[slug]);
  useEffect(() => {
    if (_cache[slug] !== undefined) { setData(_cache[slug]); return; }
    const key = `page_${slug}`;
    if (!_inflight[slug]) {
      _inflight[slug] = http.get("/public/content")
        .then(({ data: all }) => {
          // Cache every page in the response so siblings hit memory
          Object.keys(all || {}).forEach(k => {
            if (k.startsWith("page_")) _cache[k.slice(5)] = all[k] || null;
          });
          if (_cache[slug] === undefined) _cache[slug] = null;
          return _cache[slug];
        }).catch(() => null);
    }
    _inflight[slug].then(v => setData(v));
  }, [slug]);
  return data; // null while loading or if no override
}

/** Has any field that the admin actually filled in. */
export function hasOverride(o) {
  if (!o || typeof o !== "object") return false;
  return Boolean(o.eyebrow || o.title || o.subtitle || o.body || o.hero_image);
}

/**
 * Admin-editable page header. Renders only if there's an override OR the caller
 * forces it with `always`. Falls back to the `defaults` for missing fields.
 */
export function PageHero({ slug, defaults = {}, always = false }) {
  const override = usePageContent(slug);
  const o = override || {};
  if (!always && !hasOverride(o)) return null;
  const eyebrow  = o.eyebrow  ?? defaults.eyebrow;
  const title    = o.title    ?? defaults.title;
  const subtitle = o.subtitle ?? defaults.subtitle;
  const body     = o.body     ?? defaults.body;
  const heroImg  = o.hero_image || defaults.hero_image;

  return (
    <section
      className={`relative ${heroImg ? "bg-black" : "bg-[#050505]"} pt-20 md:pt-28 pb-12 md:pb-16 border-b border-white/5`}
      data-testid={`page-hero-${slug}`}
    >
      {heroImg && (
        <>
          <img
            src={resolveImage(heroImg)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black"/>
        </>
      )}
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-10">
        {eyebrow && <div className="font-ui text-[11px] tracking-[0.32em] text-[#C9A055] mb-4" data-testid={`page-hero-${slug}-eyebrow`}>{eyebrow}</div>}
        {title && (
          <h1 className="font-display text-5xl md:text-7xl text-[#F4F1EA] leading-[0.95] mb-4 whitespace-pre-line" data-testid={`page-hero-${slug}-title`}>
            {title}
          </h1>
        )}
        {subtitle && (
          <p className="font-script text-[#C9A055] text-2xl md:text-3xl mb-6" data-testid={`page-hero-${slug}-subtitle`}>
            {subtitle}
          </p>
        )}
        {body && (
          <div className="cms-body max-w-[700px]" data-testid={`page-hero-${slug}-body`} dangerouslySetInnerHTML={{ __html: body }} />
        )}
      </div>
    </section>
  );
}

/** Tells the parent page whether the override is active — for cases where the
 *  page wants to swap its existing hardcoded hero rather than render BOTH. */
export function useHasPageOverride(slug) {
  const data = usePageContent(slug);
  return hasOverride(data);
}

export function clearPageContentCache() {
  Object.keys(_cache).forEach(k => delete _cache[k]);
  Object.keys(_inflight).forEach(k => delete _inflight[k]);
}
